import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserId, setUserStorage } from '../utils/userStorage';
import './OnboardingTour.css';

interface OnboardingTourProps {
  onComplete: () => void;
}

interface TourStep {
  id: string;
  title: string;
  content: React.ReactNode;
  route?: string; // Route to navigate to for this step
  highlightElement?: string;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const tourSteps: TourStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Coach Cedar',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Cedar is your coach — and only yours. It remembers your goals, quirks, and progress so every suggestion lands where you need it. Share whatever matters; Cedar folds it into your plan so you stay supported, no matter where you&apos;re starting from.</p>
        </div>
      ),
      route: '/chat'
    },
    {
      id: 'chat',
      title: 'Chat with Cedar',
      content: (
        <div className="text-sm leading-relaxed">
          <p>This is where coaching happens. Drop a question, a schedule change, or a curveball here anytime — workouts, goals, life. The little &quot;Chat with Cedar&quot; buttons around the app all land here. Need a tour of how the app works? Ask away.</p>
        </div>
      ),
      route: '/chat'
    },
    {
      id: 'move',
      title: 'Move',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Your week, built around your life. Workouts (cardio and strength), mobility, rest — the works. Today&apos;s session is up top; the rest of the week lines up below. See a green speech bubble? Tap it to chat with Cedar about that workout or that day.</p>
          <div className="tour-cc-bubble-wrap">
            <img src="/CC_ChatBubble.svg" alt="CC chat bubble" className="tour-cc-bubble" />
          </div>
        </div>
      ),
      route: '/mypath'
    },
    {
      id: 'cedar-insights',
      title: '4 — Reports',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Your week, reflected back. Cedar turns your data into a quick recap — wins, trends, what&apos;s next — and parks it here. Skim the highlights or dive deep. Want to unpack something? Hit the chat button inside the report.</p>
        </div>
      ),
      route: '/insights'
    },
    {
      id: 'sources',
      title: '5 — Research',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Where the advice comes from. This shelf holds the studies, articles, and tools that shape your plan. Browse, geek out, and ping Cedar if you&apos;d like to talk through anything you find.</p>
        </div>
      ),
      route: '/insights'
    },
    {
      id: 'settings',
      title: '6 — Account',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Link Garmin Connect, Apple Health, or Health Connect so Cedar sees the full picture. Tune notifications and explore the rest of the settings. Feedback, bug report, or feature idea? There&apos;s an option here too.</p>
        </div>
      ),
      route: '/settings'
    },
    {
      id: 'ready',
      title: 'All Set!',
      content: (
        <div className="text-sm leading-relaxed">
          <p>Time to meet your coach.</p>
        </div>
      ),
      route: '/chat'
    }
  ];

  const currentStepData = tourSteps[currentStep];

  // Navigate to the appropriate route for each step
  useEffect(() => {
    if (currentStepData.route && currentStep > 0) {
      navigate(currentStepData.route);
    }
  }, [currentStep, currentStepData.route, navigate]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tour completed - mark tour as seen but preserve existing chat messages
      const userId = getCurrentUserId();
      
      // 🚨 REMOVED: Don't clear chat messages - let them persist
      // Only reset chat state flags, not the actual messages
      setUserStorage('chat_paused', 'false', userId);
      setUserStorage('onboarding_tour_seen', 'true', userId);
      
      console.log('🎯 Tour completed via Next - navigating to chat with trigger state');
      
      // Navigate to chat and trigger the callback
      navigate('/chat', { 
        state: { 
          tourCompleted: true,
          shouldAskForName: true,
          fromTour: true
        } 
      });
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Tour skipped - mark tour as seen but preserve existing chat messages
    const userId = getCurrentUserId();
    
    // 🚨 REMOVED: Don't clear chat messages - let them persist
    // Only reset chat state flags, not the actual messages
    setUserStorage('chat_paused', 'false', userId);
    setUserStorage('onboarding_tour_seen', 'true', userId);
    
    console.log('🎯 Tour skipped - navigating to chat with trigger state');
    
    navigate('/chat', { 
      state: { 
        tourCompleted: true,
        shouldAskForName: true,
        fromTour: true
      } 
    });
    onComplete();
  };

  return (
    <div className="onboarding-tour-overlay">
      <div className="onboarding-tour-modal">
        {/* Progress indicator - more compact for 8 steps */}
        <div className="tour-header">
          <div className="tour-progress">
            <div className="progress-dots">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${index === currentStep ? 'active' : ''}`}
                />
              ))}
            </div>
            <span className="tour-step-counter">
              Step {currentStep + 1} of {tourSteps.length}
            </span>
          </div>
          <button onClick={handleSkip} className="tour-skip-button">
            Skip
          </button>
        </div>

        {/* Fixed title section (like Legal page .frame structure) */}
        <div className="tour-title-section">
          <h3 className="tour-title">{currentStepData.title}</h3>
        </div>

        {/* Scrollable content only (like Legal page .hey-there-thanks-for-wrapper) */}
        <div className="tour-body">
          <div className="tour-content">{currentStepData.content}</div>
        </div>

        {/* Navigation buttons */}
        <div className="tour-actions">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="tour-previous-button"
          >
            Previous
          </button>
          
          <button
            onClick={handleNext}
            className={currentStep === tourSteps.length - 1 ? 'tour-next-button lets-go' : 'tour-next-button'}
          >
            {currentStep === tourSteps.length - 1 ? 'Let\'s go!' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}; 